import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import {
  Activity, AlertCircle, ArrowDown, ArrowRight, Boxes, Braces, CalendarClock,
  Check, CheckCircle2, ChevronDown, ChevronRight, CircleDot, Clock3, Code2,
  Columns3, Database, ExternalLink, FileCode2, Gauge, GitBranch, HardDrive,
  History, Layers3, Menu, MoreHorizontal, Pause, Pencil, Play, Plus, RefreshCw,
  Search, Settings, ShieldCheck, Sparkles, Table2, Trash2, X, Zap,
} from 'lucide-react'
import './styles.css'

const API = '/api/v1'

const NAV = [
  { id: 'overview', label: 'Обзор', icon: Gauge, eyebrow: 'Платформа' },
  { id: 'system', label: 'Системный дашборд', icon: HardDrive },
  { id: 'data-vault', label: 'Data Vault', icon: Database, eyebrow: 'Слои данных' },
  { id: 'business-vault', label: 'Business Vault', icon: Braces },
  { id: 'data-marts', label: 'Data Marts', icon: Boxes },
  { id: 'pipelines', label: 'Пайплайны', icon: GitBranch, eyebrow: 'Оркестрация' },
  { id: 'schedules', label: 'Расписания', icon: CalendarClock },
]

const PAGE_META = {
  overview: ['Обзор платформы', 'Контроль слоёв данных, качества и последних загрузок'],
  system: ['Системный дашборд', 'Состояние СУБД, нагрузка и дисковое пространство'],
  'data-vault': ['Data Vault', 'Физические Hub, Link и Satellite из Greengage'],
  'business-vault': ['Business Vault', 'Расчётные сущности и бизнес-правила в Greengage'],
  'data-marts': ['Data Marts', 'Аналитические витрины в ClickHouse'],
  pipelines: ['Пайплайны', 'SQL-трансформации между слоями платформы'],
  schedules: ['Расписания', 'Планирование, состояние и история запусков'],
}

const GREENGAGE_TYPES = [
  'text', 'varchar(64)', 'varchar(255)', 'char(3)', 'smallint', 'integer', 'bigint',
  'numeric(18,2)', 'real', 'double precision', 'boolean', 'date', 'timestamp',
  'timestamptz', 'bytea', 'uuid', 'jsonb',
]

const CLICKHOUSE_TYPES = [
  'String', 'LowCardinality(String)', 'FixedString(3)', 'UInt8', 'UInt16', 'UInt32',
  'UInt64', 'Int8', 'Int16', 'Int32', 'Int64', 'Float32', 'Float64',
  'Decimal(18,2)', 'Date', 'DateTime', 'DateTime64(3)', 'UUID', 'Bool',
]

const api = {
  async request(path, options = {}) {
    const response = await fetch(`${API}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...options.headers },
    })
    if (response.status === 204) return null
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(payload.error || 'Не удалось выполнить запрос')
    return payload
  },
  get: (path) => api.request(path),
  post: (path, body) => api.request(path, { method: 'POST', body: JSON.stringify(body) }),
  put: (path, body) => api.request(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (path) => api.request(path, { method: 'DELETE' }),
}

function App() {
  const [page, setPage] = useState(() => window.location.hash.replace('#/', '') || 'overview')
  const [data, setData] = useState({ overview: null, system: null, dv: [], bv: [], marts: [], martSchemas: [], pipelines: [], runs: [], health: null })
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [drawer, setDrawer] = useState(null)
  const [modal, setModal] = useState(null)
  const [toast, setToast] = useState(null)

  const load = async (quiet = false) => {
    quiet ? setRefreshing(true) : setLoading(true)
    setError('')
    try {
      const [overview, system, dv, bv, marts, martSchemas, pipelines, runs, health] = await Promise.all([
        api.get('/overview'), api.get('/system/metrics'),
        api.get('/data-vault/tables'), api.get('/business-vault/tables'),
        api.get('/data-marts/tables'), api.get('/data-marts/schemas'), api.get('/pipelines'), api.get('/runs?limit=20'), api.get('/health'),
      ])
      setData({ overview, system, dv, bv, marts, martSchemas, pipelines, runs, health })
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => { load() }, [])
  useEffect(() => {
    if (page !== 'system') return undefined
    const timer = window.setInterval(async () => {
      try {
        const system = await api.get('/system/metrics')
        setData((current) => ({ ...current, system }))
      } catch {
        // Общий баннер ошибок и ручное обновление остаются основным UX.
      }
    }, 30000)
    return () => window.clearInterval(timer)
  }, [page])
  useEffect(() => {
    const onHash = () => setPage(window.location.hash.replace('#/', '') || 'overview')
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const navigate = (id) => {
    window.location.hash = `/${id}`
    setPage(id)
    setSidebarOpen(false)
    setDrawer(null)
  }

  const notify = (message, tone = 'success') => {
    setToast({ message, tone })
    window.setTimeout(() => setToast(null), 3200)
  }

  const saveTable = async (layer, table, editing) => {
    const endpoint = layer === 'business-vault' ? '/business-vault/tables' : '/data-marts/tables'
    try {
      if (editing) await api.put(`${endpoint}/${table.id}`, table)
      else await api.post(endpoint, table)
      setModal(null)
      notify(editing ? 'Изменения сохранены' : 'Таблица создана')
      await load(true)
    } catch (err) {
      notify(err.message, 'error')
      throw err
    }
  }

  const deleteTable = async (layer, table) => {
    const endpoint = layer === 'business-vault' ? '/business-vault/tables' : '/data-marts/tables'
    try {
      await api.delete(`${endpoint}/${table.id}`)
      setModal(null)
      setDrawer(null)
      notify(`Таблица ${table.name} удалена`)
      await load(true)
    } catch (err) { notify(err.message, 'error') }
  }

  const savePipeline = async (pipeline, editing) => {
    try {
      if (editing) await api.put(`/pipelines/${pipeline.id}`, pipeline)
      else await api.post('/pipelines', pipeline)
      setModal(null)
      notify(editing ? 'Пайплайн обновлён' : 'Пайплайн создан')
      await load(true)
    } catch (err) {
      notify(err.message, 'error')
      throw err
    }
  }

  const deletePipeline = async (pipeline) => {
    try {
      await api.delete(`/pipelines/${pipeline.id}`)
      setModal(null)
      setDrawer(null)
      notify(`Пайплайн ${pipeline.name} удалён`)
      await load(true)
    } catch (err) { notify(err.message, 'error') }
  }

  const runPipeline = async (pipeline) => {
    notify(`Запускаем «${pipeline.name}»…`, 'info')
    try {
      const run = await api.post(`/pipelines/${pipeline.id}/run`, {})
      notify(`Готово: записано ${formatNumber(run.rowsWritten)} строк`)
      await load(true)
    } catch (err) { notify(err.message, 'error') }
  }

  const togglePipeline = async (pipeline) => {
    try {
      await api.post(`/pipelines/${pipeline.id}/toggle`, {})
      notify(pipeline.schedule.enabled ? 'Расписание приостановлено' : 'Расписание включено')
      await load(true)
    } catch (err) { notify(err.message, 'error') }
  }

  const title = PAGE_META[page] || PAGE_META.overview
  const mode = data.health?.connections?.mode === 'demo' ? 'Демо-среда' : 'Production'

  return (
    <div className="app-shell">
      <Sidebar page={page} navigate={navigate} open={sidebarOpen} onClose={() => setSidebarOpen(false)} mode={mode} health={data.health} />
      <main className="main">
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setSidebarOpen(true)} aria-label="Открыть меню"><Menu size={20} /></button>
          <div className="title-block">
            <h1>{title[0]}</h1>
            <p>{title[1]}</p>
          </div>
          <div className="topbar-actions">
            <div className="environment-pill"><span className="live-dot" /> {mode}<ChevronDown size={14} /></div>
            <button className="icon-button" onClick={() => load(true)} aria-label="Обновить данные"><RefreshCw size={18} className={refreshing ? 'spin' : ''} /></button>
            <div className="avatar">AK</div>
          </div>
        </header>

        <div className="content">
          {error && <ErrorBanner message={error} retry={() => load()} />}
          {loading ? <PageSkeleton /> : !data.overview ? (
            <section className="card offline-card"><EmptyState icon={HardDrive} title="Нет данных для отображения" text="После подключения API каталог и метрики появятся автоматически." /></section>
          ) : (
            <>
              {page === 'overview' && <Overview data={data} onNavigate={navigate} onRun={runPipeline} />}
              {page === 'system' && <SystemDashboard metrics={data.system} />}
              {page === 'data-vault' && (
                <TableCatalog layer="data-vault" tables={data.dv} onSelect={(table) => setDrawer({ type: 'table', layer: 'data-vault', item: table })} />
              )}
              {page === 'business-vault' && (
                <TableCatalog
                  layer="business-vault" tables={data.bv}
                  onSelect={(table) => setDrawer({ type: 'table', layer: 'business-vault', item: table })}
                  onCreate={() => setModal({ type: 'table', layer: 'business-vault' })}
                />
              )}
              {page === 'data-marts' && (
                <TableCatalog
                  layer="data-mart" tables={data.marts}
                  onSelect={(table) => setDrawer({ type: 'table', layer: 'data-mart', item: table })}
                  onCreate={() => setModal({ type: 'table', layer: 'data-mart' })}
                />
              )}
              {page === 'pipelines' && (
                <Pipelines
                  pipelines={data.pipelines} onCreate={() => setModal({ type: 'pipeline' })}
                  onOpen={(item) => setDrawer({ type: 'pipeline', item })} onRun={runPipeline} onToggle={togglePipeline}
                />
              )}
              {page === 'schedules' && (
                <Schedules pipelines={data.pipelines} runs={data.runs} onRun={runPipeline} onToggle={togglePipeline} onOpen={(item) => setDrawer({ type: 'pipeline', item })} />
              )}
            </>
          )}
        </div>
      </main>

      {drawer?.type === 'table' && (
        <TableDrawer
          table={drawer.item} layer={drawer.layer} onClose={() => setDrawer(null)}
          onEdit={drawer.layer === 'data-vault' ? null : () => setModal({ type: 'table', layer: drawer.layer, item: drawer.item })}
          onDelete={drawer.layer === 'data-vault' ? null : () => setModal({ type: 'confirm-table', layer: drawer.layer, item: drawer.item })}
          onPipeline={() => setModal({ type: 'pipeline', source: drawer.item, sourceLayer: drawer.layer })}
        />
      )}
      {drawer?.type === 'pipeline' && (
        <PipelineDrawer
          pipeline={drawer.item} onClose={() => setDrawer(null)} onRun={runPipeline} onToggle={togglePipeline}
          onEdit={() => setModal({ type: 'pipeline', item: drawer.item })}
          onDelete={() => setModal({ type: 'confirm-pipeline', item: drawer.item })}
        />
      )}
      {modal?.type === 'table' && <TableModal layer={modal.layer} initial={modal.item} schemas={data.martSchemas} onClose={() => setModal(null)} onSave={saveTable} />}
      {modal?.type === 'pipeline' && (
        <PipelineModal
          initial={modal.item} source={modal.source} sourceLayer={modal.sourceLayer}
          tables={data} onClose={() => setModal(null)} onSave={savePipeline}
        />
      )}
      {modal?.type === 'confirm-table' && (
        <ConfirmModal title="Удалить таблицу?" text={`Таблица ${modal.item.schema}.${modal.item.name} будет удалена из базы данных. Это действие нельзя отменить.`} confirm="Удалить таблицу" onClose={() => setModal(null)} onConfirm={() => deleteTable(modal.layer, modal.item)} />
      )}
      {modal?.type === 'confirm-pipeline' && (
        <ConfirmModal title="Удалить пайплайн?" text={`Пайплайн «${modal.item.name}» и его расписание будут удалены. История запусков сохранится.`} confirm="Удалить пайплайн" onClose={() => setModal(null)} onConfirm={() => deletePipeline(modal.item)} />
      )}
      {toast && <Toast {...toast} />}
    </div>
  )
}

function Sidebar({ page, navigate, open, onClose, mode, health }) {
  const connection = (id) => health?.connections?.[id] || 'unavailable'
  const connectionLabel = (id) => {
    const value = connection(id)
    return value === 'connected' ? 'Online' : value === 'demo' ? 'Demo' : value === 'not_configured' ? 'Не настроено' : 'Offline'
  }
  return (
    <>
      {open && <div className="sidebar-backdrop" onClick={onClose} />}
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="brand">
          <div className="brand-mark"><Layers3 size={22} strokeWidth={2.2} /></div>
          <div><strong>NBT</strong><span>DATA PLATFORM</span></div>
          <button className="icon-button sidebar-close" onClick={onClose}><X size={19} /></button>
        </div>
        <nav>
          {NAV.map((item, index) => {
            const Icon = item.icon
            return (
              <React.Fragment key={item.id}>
                {item.eyebrow && <div className={`nav-eyebrow ${index ? 'nav-gap' : ''}`}>{item.eyebrow}</div>}
                <button className={`nav-item ${page === item.id ? 'active' : ''}`} onClick={() => navigate(item.id)}>
                  <Icon size={18} /><span>{item.label}</span>
                  {page === item.id && <ChevronRight className="nav-chevron" size={15} />}
                </button>
              </React.Fragment>
            )
          })}
        </nav>
        <div className="sidebar-spacer" />
        <div className="platform-status">
          <div className="platform-status-head"><Activity size={16} /><span>Состояние платформы</span></div>
          <div className="status-row"><span><i className={`status-dot ${['connected', 'demo'].includes(connection('metadata')) ? 'healthy' : 'failed'}`} />Metadata DB</span><b>{connectionLabel('metadata')}</b></div>
          <div className="status-row"><span><i className={`status-dot ${['connected', 'demo'].includes(connection('greengage')) ? 'healthy' : 'failed'}`} />Greengage</span><b>{connectionLabel('greengage')}</b></div>
          <div className="status-row"><span><i className={`status-dot ${['connected', 'demo'].includes(connection('clickhouse')) ? 'healthy' : 'failed'}`} />ClickHouse</span><b>{connectionLabel('clickhouse')}</b></div>
        </div>
        <button className="nav-item" onClick={() => window.open('/swagger/', '_blank', 'noopener,noreferrer')}>
          <FileCode2 size={18} /><span>Swagger API</span><ExternalLink className="nav-chevron" size={14} />
        </button>
        <button className="nav-item settings-item"><Settings size={18} /><span>Настройки</span></button>
      </aside>
    </>
  )
}

function SystemDashboard({ metrics }) {
  if (!metrics) {
    return <section className="card offline-card"><EmptyState icon={HardDrive} title="Системные метрики недоступны" text="Обновите страницу после подключения хранилищ." /></section>
  }
  const totals = metrics.totals
  const healthy = metrics.storages.filter((storage) => ['healthy', 'demo'].includes(storage.status)).length
  const statusCopy = metrics.status === 'healthy'
    ? ['Все системы работают штатно', `${healthy} из ${metrics.storages.length} хранилищ доступны`]
    : metrics.status === 'demo'
      ? ['Платформа работает в демо-режиме', 'Физические метрики доступны только для подключённых систем']
      : ['Обнаружены проблемы с хранилищами', `${healthy} из ${metrics.storages.length} хранилищ доступны`]
  return (
    <div className="page-stack">
      <section className={`system-health-hero ${metrics.status}`}>
        <div className="system-health-icon"><ShieldCheck size={24} /></div>
        <div><span className="kicker">СОСТОЯНИЕ ПЛАТФОРМЫ</span><h2>{statusCopy[0]}</h2><p>{statusCopy[1]}</p></div>
        <div className="system-collected"><RefreshCw size={14} /><span>Снимок {formatRelative(metrics.collectedAt)}</span><small>Автообновление каждые 30 секунд</small></div>
      </section>

      <section className="system-metrics-grid">
        <SystemMetric icon={HardDrive} tone="blue" label="Объём данных" value={formatBytes(totals.databaseBytes)} detail="во всех базах" />
        <SystemMetric icon={Columns3} tone="violet" label="Строки" value={formatCompact(totals.rowCount)} detail={formatNumber(totals.rowCount)} />
        <SystemMetric icon={Table2} tone="cyan" label="Физические таблицы" value={formatNumber(totals.tableCount)} detail="пользовательские схемы" />
        <SystemMetric icon={Gauge} tone={totals.diskUsedPercent >= 85 ? 'red' : 'green'} label="Диск занят" value={totals.diskTotalBytes ? `${formatPercent(totals.diskUsedPercent)}%` : '—'} detail={totals.diskTotalBytes ? `${formatBytes(totals.diskFreeBytes)} свободно` : 'ёмкость не сообщается'} />
      </section>

      <section className="storage-grid">
        {metrics.storages.map((storage) => <StorageCard key={storage.id} storage={storage} />)}
      </section>
    </div>
  )
}

function SystemMetric({ icon: Icon, tone, label, value, detail }) {
  return (
    <div className="system-metric-card">
      <div className={`metric-icon ${tone}`}><Icon size={21} /></div>
      <div className="metric-copy"><span>{label}</span><strong>{value}</strong><small>{detail}</small></div>
    </div>
  )
}

function StorageCard({ storage }) {
  const available = ['healthy', 'demo'].includes(storage.status)
  const labels = { healthy: 'Online', demo: 'Demo', unavailable: 'Недоступно', not_configured: 'Не настроено' }
  return (
    <article className={`storage-card ${available ? '' : 'unavailable'}`}>
      <header className="storage-card-header">
        <div className={`storage-logo ${storage.id}`}><Database size={21} /></div>
        <div className="storage-heading"><span>{storage.role}</span><h3>{storage.name}</h3><small>{storage.database || '—'} · {storage.version || 'версия неизвестна'}</small></div>
        <span className={`storage-status ${storage.status}`}><i />{labels[storage.status] || storage.status}</span>
      </header>
      {!available ? (
        <div className="storage-unavailable"><AlertCircle size={19} /><strong>{storage.statusMessage || 'Метрики временно недоступны'}</strong><span>Проверьте подключение и параметры окружения backend.</span></div>
      ) : (
        <>
          <div className="storage-kpis">
            <StorageKPI label="Размер базы" value={formatBytes(storage.databaseBytes)} />
            <StorageKPI label="Строк" value={formatCompact(storage.rowCount)} title={formatNumber(storage.rowCount)} />
            <StorageKPI label="Таблиц" value={formatNumber(storage.tableCount)} />
            <StorageKPI label="Соединения" value={`${formatNumber(storage.activeConnections)} / ${formatNumber(storage.maxConnections)}`} />
            <StorageKPI label="Uptime" value={formatUptime(storage.uptimeSeconds)} />
            <StorageKPI label="Отклик" value={`${storage.latencyMs} мс`} />
          </div>
          <div className="storage-section">
            <div className="storage-section-title"><span><HardDrive size={15} />Дисковое пространство</span><b>{storage.disks.length || '—'}</b></div>
            {storage.disks.length ? storage.disks.map((disk) => (
              <div className="disk-row" key={`${disk.name}-${disk.path}`}>
                <div className="disk-copy"><strong>{disk.name}</strong><span>{disk.path}</span></div>
                {disk.totalBytes > 0 ? (
                  <>
                    <div className="disk-progress"><i style={{ width: `${Math.min(disk.usedPercent, 100)}%` }} /></div>
                    <div className="disk-values"><strong>{formatPercent(disk.usedPercent)}% занято</strong><span>{formatBytes(disk.freeBytes)} из {formatBytes(disk.totalBytes)} свободно</span></div>
                  </>
                ) : <div className="disk-values free-only"><strong>{formatBytes(disk.freeBytes)} свободно</strong><span>Общая ёмкость не сообщается</span></div>}
              </div>
            )) : <div className="storage-empty">СУБД не предоставляет сведения о дисках</div>}
          </div>
          <div className="storage-section activity-section">
            <div className="storage-section-title"><span><Activity size={15} />Активность с момента старта</span></div>
            <div className="activity-grid">
              <StorageKPI label="Операций / запросов" value={formatCompact(storage.queryCount)} />
              <StorageKPI label="Прочитано строк" value={formatCompact(storage.readRows)} />
              <StorageKPI label="Записано строк" value={formatCompact(storage.writtenRows)} />
              <StorageKPI label="Ошибок" value={formatNumber(storage.failedQueries)} danger={storage.failedQueries > 0} />
            </div>
          </div>
        </>
      )}
    </article>
  )
}

function StorageKPI({ label, value, title, danger }) {
  return <div className={`storage-kpi ${danger ? 'danger' : ''}`} title={title}><span>{label}</span><strong>{value}</strong></div>
}

function Overview({ data, onNavigate, onRun }) {
  const o = data.overview
  const recent = data.runs.slice(0, 4)
  const attention = [...data.dv, ...data.bv, ...data.marts].filter((t) => t.status !== 'healthy').slice(0, 3)
  return (
    <div className="page-stack">
      <section className="metrics-grid">
        <MetricCard icon={Database} tone="blue" label="Data Vault" value={o.dataVaultTables} detail="физических таблиц" onClick={() => onNavigate('data-vault')} />
        <MetricCard icon={Braces} tone="violet" label="Business Vault" value={o.businessTables} detail="бизнес-сущности" onClick={() => onNavigate('business-vault')} />
        <MetricCard icon={Boxes} tone="cyan" label="Data Marts" value={o.dataMarts} detail="витрины ClickHouse" onClick={() => onNavigate('data-marts')} />
        <MetricCard icon={CalendarClock} tone="green" label="Расписания" value={o.activeSchedules} detail="активных задания" onClick={() => onNavigate('schedules')} />
      </section>

      <section className="architecture-card">
        <div className="section-heading">
          <div><span className="kicker">АРХИТЕКТУРА</span><h2>Поток данных</h2></div>
          <div className="sync-caption"><RefreshCw size={14} /> Синхронизировано {formatRelative(o.lastSyncAt)}</div>
        </div>
        <div className="layer-flow">
          <LayerNode tone="blue" icon={Database} title="Data Vault" subtitle="Greengage" count={`${o.dataVaultTables} таблиц`} meta="Hub · Link · Satellite" health={`${data.dv.filter((t) => t.status === 'healthy').length}/${data.dv.length} healthy`} onClick={() => onNavigate('data-vault')} />
          <FlowConnector label={`${data.pipelines.filter((p) => p.targetLayer === 'business-vault').length} pipelines`} />
          <LayerNode tone="violet" icon={Braces} title="Business Vault" subtitle="Greengage" count={`${o.businessTables} таблицы`} meta="PIT · Bridge · Aggregate" health={`${data.bv.filter((t) => t.status === 'healthy').length}/${data.bv.length} healthy`} onClick={() => onNavigate('business-vault')} />
          <FlowConnector label={`${data.pipelines.filter((p) => p.targetLayer === 'data-mart').length} pipelines`} />
          <LayerNode tone="cyan" icon={Boxes} title="Data Marts" subtitle="ClickHouse" count={`${o.dataMarts} витрины`} meta="MergeTree · BI-ready" health={`${data.marts.filter((t) => t.status === 'healthy').length}/${data.marts.length} healthy`} onClick={() => onNavigate('data-marts')} />
        </div>
      </section>

      <div className="overview-columns">
        <section className="card">
          <div className="section-heading compact">
            <div><span className="kicker">АКТИВНОСТЬ</span><h2>Последние запуски</h2></div>
            <button className="text-button" onClick={() => onNavigate('schedules')}>Вся история <ArrowRight size={15} /></button>
          </div>
          <div className="run-list">
            {recent.map((run) => {
              const pipeline = data.pipelines.find((item) => item.id === run.pipelineId)
              return <RunRow key={run.id} run={run} onRun={pipeline ? () => onRun(pipeline) : null} />
            })}
          </div>
        </section>
        <section className="card">
          <div className="section-heading compact">
            <div><span className="kicker">КАЧЕСТВО</span><h2>Требует внимания</h2></div>
            <HealthDonut healthy={o.healthy} warning={o.warnings} failed={o.failed} />
          </div>
          <div className="attention-list">
            {attention.length ? attention.map((table) => (
              <button key={table.id} className="attention-row" onClick={() => onNavigate(table.engine === 'Greengage' && table.schema === 'raw_vault' ? 'data-vault' : table.engine === 'Greengage' ? 'business-vault' : 'data-marts')}>
                <div className="attention-icon"><AlertCircle size={17} /></div>
                <div><strong>{table.name}</strong><span>{table.status === 'warning' ? 'Загрузка отстаёт от SLA' : 'Последняя загрузка завершилась ошибкой'}</span></div>
                <ChevronRight size={16} />
              </button>
            )) : <EmptyInline icon={CheckCircle2} text="Все объекты работают штатно" />}
          </div>
        </section>
      </div>
    </div>
  )
}

function MetricCard({ icon: Icon, tone, label, value, detail, onClick }) {
  return (
    <button className="metric-card" onClick={onClick}>
      <div className={`metric-icon ${tone}`}><Icon size={21} /></div>
      <div className="metric-copy"><span>{label}</span><strong>{value}</strong><small>{detail}</small></div>
      <ArrowRight className="metric-arrow" size={17} />
    </button>
  )
}

function LayerNode({ icon: Icon, tone, title, subtitle, count, meta, health, onClick }) {
  return (
    <button className={`layer-node ${tone}`} onClick={onClick}>
      <div className="layer-node-top"><div className="layer-icon"><Icon size={22} /></div><span className="db-badge">{subtitle}</span></div>
      <h3>{title}</h3><strong>{count}</strong><p>{meta}</p>
      <div className="layer-health"><span className="status-dot healthy" />{health}<ArrowRight size={15} /></div>
    </button>
  )
}

function FlowConnector({ label }) {
  return <div className="flow-connector"><span>{label}</span><div><i /><ArrowRight size={18} /></div></div>
}

function HealthDonut({ healthy, warning, failed }) {
  const total = Math.max(healthy + warning + failed, 1)
  const percentage = Math.round((healthy / total) * 100)
  return <div className="health-donut" style={{ '--percent': `${percentage * 3.6}deg` }}><span>{percentage}%</span></div>
}

function RunRow({ run, onRun }) {
  const success = run.status === 'success'
  return (
    <div className="run-row">
      <div className={`run-status-icon ${run.status}`}>{success ? <Check size={15} /> : <X size={15} />}</div>
      <div className="run-primary"><strong>{run.pipelineName}</strong><span>{formatDateTime(run.startedAt)} · {run.trigger === 'schedule' ? 'По расписанию' : 'Вручную'}</span></div>
      <div className="run-stats"><strong>{success ? formatNumber(run.rowsWritten) : 'Ошибка'}</strong><span>{success ? 'строк' : run.error}</span></div>
      <div className="run-duration"><Clock3 size={14} /> {formatDuration(run.durationMs)}</div>
      {!success && onRun && <button className="icon-button subtle" onClick={onRun} title="Повторить"><RefreshCw size={15} /></button>}
    </div>
  )
}

function TableCatalog({ layer, tables, onSelect, onCreate }) {
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState('all')
  const kinds = ['all', ...new Set(tables.map((table) => table.kind))]
  const filtered = tables.filter((table) => {
    const query = search.toLowerCase()
    return (kind === 'all' || table.kind === kind) && (`${table.schema}.${table.name} ${table.description}`.toLowerCase().includes(query))
  })
  const copy = layer === 'data-vault'
    ? { eyebrow: 'READ ONLY', note: 'Структура синхронизируется напрямую из Greengage', button: null }
    : layer === 'business-vault'
      ? { eyebrow: 'GREEN GAGE', note: 'Управляемые бизнес-таблицы и расчётные сущности', button: 'Создать таблицу' }
      : { eyebrow: 'CLICKHOUSE', note: 'Витрины, оптимизированные для аналитических запросов', button: 'Создать витрину' }
  return (
    <div className="page-stack">
      <section className="catalog-intro">
        <div><span className="kicker">{copy.eyebrow}</span><h2>{tables.length} {pluralTable(tables.length)}</h2><p>{copy.note}</p></div>
        {onCreate && <button className="primary-button" onClick={onCreate}><Plus size={17} />{copy.button}</button>}
      </section>
      <section className="card catalog-card">
        <div className="catalog-toolbar">
          <label className="search-field"><Search size={17} /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Поиск по имени или описанию…" /><kbd>⌘ K</kbd></label>
          <div className="filter-tabs">
            {kinds.map((item) => <button key={item} className={kind === item ? 'active' : ''} onClick={() => setKind(item)}>{item === 'all' ? 'Все' : item}</button>)}
          </div>
          <span className="result-count">{filtered.length} из {tables.length}</span>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead><tr><th>Таблица</th><th>Тип</th><th>Владелец</th><th>Строк</th><th>Размер</th><th>Обновлено</th><th>Статус</th><th /></tr></thead>
            <tbody>
              {filtered.map((table) => (
                <tr key={table.id} onClick={() => onSelect(table)}>
                  <td><div className="table-name-cell"><div className={`table-glyph ${toneForKind(table.kind)}`}><Table2 size={17} /></div><div><strong>{table.name}</strong><span>{table.schema} · {table.columns.length} колонок</span></div></div></td>
                  <td><span className={`kind-badge ${toneForKind(table.kind)}`}>{table.kind}</span></td>
                  <td>{table.owner}</td>
                  <td className="mono-number">{formatCompact(table.rows)}</td>
                  <td>{table.size}</td>
                  <td>{formatRelative(table.updatedAt)}</td>
                  <td><StatusBadge status={table.status} /></td>
                  <td><ChevronRight size={16} className="row-chevron" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filtered.length && <EmptyState icon={Search} title="Ничего не найдено" text="Попробуйте изменить запрос или фильтр типа таблицы." />}
        </div>
      </section>
    </div>
  )
}

function Pipelines({ pipelines, onCreate, onOpen, onRun, onToggle }) {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const filtered = pipelines.filter((p) => (filter === 'all' || p.status === filter) && p.name.toLowerCase().includes(search.toLowerCase()))
  return (
    <div className="page-stack">
      <section className="catalog-intro">
        <div><span className="kicker">SQL TRANSFORMATIONS</span><h2>{pipelines.length} {pluralPipeline(pipelines.length)}</h2><p>Управляйте логикой переноса данных между слоями</p></div>
        <button className="primary-button" onClick={onCreate}><Plus size={17} />Создать пайплайн</button>
      </section>
      <section className="pipeline-summary">
        <SummaryChip icon={Activity} label="Активны" value={pipelines.filter((p) => p.status === 'active').length} tone="green" />
        <SummaryChip icon={Pause} label="На паузе" value={pipelines.filter((p) => p.status === 'paused').length} tone="neutral" />
        <SummaryChip icon={AlertCircle} label="С ошибкой" value={pipelines.filter((p) => p.status === 'failed').length} tone="red" />
        <SummaryChip icon={Zap} label="Записано за сутки" value={formatCompact(pipelines.reduce((sum, p) => sum + (p.rowsWritten || 0), 0))} tone="violet" />
      </section>
      <section className="card catalog-card">
        <div className="catalog-toolbar">
          <label className="search-field"><Search size={17} /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Найти пайплайн…" /></label>
          <div className="filter-tabs">
            {[['all', 'Все'], ['active', 'Активные'], ['paused', 'На паузе'], ['failed', 'Ошибки']].map(([id, label]) => <button key={id} className={filter === id ? 'active' : ''} onClick={() => setFilter(id)}>{label}</button>)}
          </div>
        </div>
        <div className="pipeline-list">
          {filtered.map((pipeline) => (
            <div className="pipeline-row" key={pipeline.id} onClick={() => onOpen(pipeline)}>
              <div className={`pipeline-main-icon ${pipeline.targetLayer === 'data-mart' ? 'cyan' : 'violet'}`}><GitBranch size={19} /></div>
              <div className="pipeline-name"><strong>{pipeline.name}</strong><span>{pipeline.description}</span></div>
              <div className="mini-flow"><LayerPill layer={pipeline.sourceLayer} /><ArrowRight size={15} /><LayerPill layer={pipeline.targetLayer} /></div>
              <div className="pipeline-schedule"><Clock3 size={14} /><div><strong>{pipeline.schedule.enabled ? cronLabel(pipeline.schedule.cron) : 'Без расписания'}</strong><span>{pipeline.schedule.enabled && pipeline.nextRunAt ? `Следующий ${formatRelative(pipeline.nextRunAt)}` : 'Запуск вручную'}</span></div></div>
              <StatusBadge status={pipeline.status} />
              <div className="row-actions">
                <button className="icon-button subtle" title={pipeline.schedule.enabled ? 'Поставить на паузу' : 'Включить'} onClick={(e) => { e.stopPropagation(); onToggle(pipeline) }}>{pipeline.schedule.enabled ? <Pause size={16} /> : <Play size={16} />}</button>
                <button className="run-button" onClick={(e) => { e.stopPropagation(); onRun(pipeline) }}><Play size={14} fill="currentColor" />Запустить</button>
              </div>
            </div>
          ))}
          {!filtered.length && <EmptyState icon={GitBranch} title="Пайплайны не найдены" text="Измените фильтр или создайте новый SQL-пайплайн." />}
        </div>
      </section>
    </div>
  )
}

function Schedules({ pipelines, runs, onRun, onToggle, onOpen }) {
  const scheduled = pipelines.filter((p) => p.schedule.enabled).sort((a, b) => new Date(a.nextRunAt) - new Date(b.nextRunAt))
  const [tab, setTab] = useState('schedule')
  return (
    <div className="page-stack">
      <section className="schedule-hero">
        <div><span className="kicker">ORCHESTRATION</span><h2>Контроль загрузок</h2><p>Все задания используют cron и часовой пояс, указанный для пайплайна.</p></div>
        <div className="schedule-hero-stat"><span>Ближайший запуск</span><strong>{scheduled[0]?.nextRunAt ? formatDateTime(scheduled[0].nextRunAt) : '—'}</strong><small>{scheduled[0]?.name || 'Нет активных расписаний'}</small></div>
      </section>
      <div className="view-tabs">
        <button className={tab === 'schedule' ? 'active' : ''} onClick={() => setTab('schedule')}><CalendarClock size={16} />Расписание</button>
        <button className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}><History size={16} />История запусков</button>
      </div>
      {tab === 'schedule' ? (
        <section className="schedule-grid">
          <div className="card schedule-list-card">
            <div className="section-heading compact"><div><span className="kicker">СЛЕДУЮЩИЕ</span><h2>Очередь заданий</h2></div><span className="count-badge">{scheduled.length}</span></div>
            <div className="schedule-list">
              {scheduled.map((pipeline, i) => (
                <div className="schedule-item" key={pipeline.id} onClick={() => onOpen(pipeline)}>
                  <div className="schedule-time"><strong>{pipeline.nextRunAt ? formatClock(pipeline.nextRunAt) : '—'}</strong><span>{pipeline.nextRunAt ? formatDay(pipeline.nextRunAt) : ''}</span></div>
                  <div className="timeline-mark"><i />{i < scheduled.length - 1 && <b />}</div>
                  <div className="schedule-copy"><strong>{pipeline.name}</strong><span>{pipeline.sourceTable} → {pipeline.targetTable}</span><small><Code2 size={13} />{pipeline.schedule.cron} · {pipeline.schedule.timezone}</small></div>
                  <button className="icon-button subtle" onClick={(e) => { e.stopPropagation(); onRun(pipeline) }} title="Запустить сейчас"><Play size={16} /></button>
                </div>
              ))}
              {!scheduled.length && <EmptyState icon={CalendarClock} title="Нет активных расписаний" text="Включите расписание в настройках пайплайна." />}
            </div>
          </div>
          <div className="card all-jobs-card">
            <div className="section-heading compact"><div><span className="kicker">ЗАДАНИЯ</span><h2>Все расписания</h2></div></div>
            <div className="job-list">
              {pipelines.map((pipeline) => (
                <div className="job-row" key={pipeline.id}>
                  <button className={`switch ${pipeline.schedule.enabled ? 'on' : ''}`} onClick={() => onToggle(pipeline)}><span /></button>
                  <button className="job-copy" onClick={() => onOpen(pipeline)}><strong>{pipeline.name}</strong><span>{pipeline.schedule.cron || 'Не задано'} · {pipeline.schedule.timezone}</span></button>
                  <StatusBadge status={pipeline.status} />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="card history-card">
          <div className="data-table-wrap">
            <table className="data-table history-table">
              <thead><tr><th>Пайплайн</th><th>Начало</th><th>Триггер</th><th>Длительность</th><th>Записано</th><th>Результат</th></tr></thead>
              <tbody>{runs.map((run) => <tr key={run.id}><td><strong>{run.pipelineName}</strong></td><td>{formatDateTime(run.startedAt)}</td><td>{run.trigger === 'schedule' ? 'Расписание' : 'Вручную'}</td><td>{formatDuration(run.durationMs)}</td><td>{run.status === 'success' ? formatNumber(run.rowsWritten) : '—'}</td><td><StatusBadge status={run.status} /></td></tr>)}</tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  )
}

function TableDrawer({ table, layer, onClose, onEdit, onDelete, onPipeline }) {
  return (
    <Drawer onClose={onClose}>
      <div className="drawer-header">
        <div className={`drawer-icon ${layer === 'data-mart' ? 'cyan' : layer === 'business-vault' ? 'violet' : 'blue'}`}><Table2 size={22} /></div>
        <div className="drawer-heading"><span>{table.schema}</span><h2>{table.name}</h2></div>
        <button className="icon-button" onClick={onClose}><X size={19} /></button>
      </div>
      <div className="drawer-actions">
        {onEdit && <button className="secondary-button" onClick={onEdit}><Pencil size={15} />Редактировать</button>}
        <button className="primary-button" onClick={onPipeline}><GitBranch size={15} />Создать пайплайн</button>
        {onDelete && <button className="icon-button danger-ghost" onClick={onDelete} title="Удалить"><Trash2 size={17} /></button>}
      </div>
      <div className="drawer-body">
        <div className="drawer-status-line"><StatusBadge status={table.status} /><span>Обновлено {formatRelative(table.updatedAt)}</span></div>
        <p className="drawer-description">{table.description || 'Описание не добавлено'}</p>
        <div className="property-grid">
          <Property label="Движок" value={table.engine} />
          <Property label="Тип" value={table.kind} />
          <Property label="Строк" value={formatNumber(table.rows)} />
          <Property label="Размер" value={table.size || '—'} />
          <Property label="Владелец" value={table.owner} wide />
        </div>
        <div className="drawer-section-title"><div><span className="kicker">СХЕМА</span><h3>Колонки</h3></div><span>{table.columns.length}</span></div>
        <div className="column-list">
          {table.columns.map((column) => (
            <div className="column-row" key={column.name}>
              <div className="column-icon"><Columns3 size={15} /></div>
              <div><strong>{column.name}</strong><span>{column.description || (column.key ? 'Ключевое поле' : 'Атрибут')}</span></div>
              {column.key && <span className="key-badge">KEY</span>}
              {column.nullable && <span className="nullable-badge">NULL</span>}
              <code>{column.type}</code>
            </div>
          ))}
        </div>
        {table.orderBy?.length > 0 && <div className="order-box"><span>ORDER BY</span><code>{table.orderBy.join(', ')}</code></div>}
      </div>
    </Drawer>
  )
}

function PipelineDrawer({ pipeline, onClose, onEdit, onDelete, onRun, onToggle }) {
  return (
    <Drawer wide onClose={onClose}>
      <div className="drawer-header">
        <div className="drawer-icon violet"><GitBranch size={22} /></div>
        <div className="drawer-heading"><span>SQL PIPELINE</span><h2>{pipeline.name}</h2></div>
        <button className="icon-button" onClick={onClose}><X size={19} /></button>
      </div>
      <div className="drawer-actions">
        <button className="secondary-button" onClick={onEdit}><Pencil size={15} />Редактировать</button>
        <button className="primary-button" onClick={() => onRun(pipeline)}><Play size={15} fill="currentColor" />Запустить</button>
        <button className="icon-button danger-ghost" onClick={onDelete}><Trash2 size={17} /></button>
      </div>
      <div className="drawer-body">
        <div className="pipeline-drawer-flow">
          <div><span>ИСТОЧНИК</span><strong>{pipeline.sourceTable}</strong><LayerPill layer={pipeline.sourceLayer} /></div>
          <ArrowRight size={22} />
          <div><span>НАЗНАЧЕНИЕ</span><strong>{pipeline.targetTable}</strong><LayerPill layer={pipeline.targetLayer} /></div>
        </div>
        <p className="drawer-description">{pipeline.description}</p>
        <div className="schedule-panel">
          <div className="schedule-panel-icon"><CalendarClock size={18} /></div>
          <div><span>Расписание</span><strong>{pipeline.schedule.enabled ? cronLabel(pipeline.schedule.cron) : 'Отключено'}</strong><small>{pipeline.schedule.cron} · {pipeline.schedule.timezone}</small></div>
          <button className={`switch ${pipeline.schedule.enabled ? 'on' : ''}`} onClick={() => onToggle(pipeline)}><span /></button>
        </div>
        <div className="property-grid">
          <Property label="Последний запуск" value={pipeline.lastRunAt ? formatRelative(pipeline.lastRunAt) : 'Ещё не запускался'} />
          <Property label="Следующий запуск" value={pipeline.nextRunAt ? formatDateTime(pipeline.nextRunAt) : '—'} />
          <Property label="Длительность" value={pipeline.durationMs ? formatDuration(pipeline.durationMs) : '—'} />
          <Property label="Записано строк" value={pipeline.rowsWritten ? formatNumber(pipeline.rowsWritten) : '—'} />
        </div>
        <div className="drawer-section-title"><div><span className="kicker">ТРАНСФОРМАЦИЯ</span><h3>SQL-запрос</h3></div><FileCode2 size={17} /></div>
        <CodePreview code={pipeline.sql} />
      </div>
    </Drawer>
  )
}

function Drawer({ children, wide }) {
  return <><div className="drawer-backdrop" /><aside className={`drawer ${wide ? 'wide' : ''}`}>{children}</aside></>
}

function TableModal({ layer, initial, schemas = [], onClose, onSave }) {
  const clickhouse = layer === 'data-mart'
  const dataTypes = clickhouse ? CLICKHOUSE_TYPES : GREENGAGE_TYPES
  const availableSchemas = [...new Set([initial?.schema, ...schemas, 'analytics'].filter(Boolean))]
  const [form, setForm] = useState(() => initial ? structuredClone(initial) : {
    name: '', schema: clickhouse ? (schemas[0] || 'analytics') : 'business_vault',
    kind: clickhouse ? 'Mart' : 'PIT', engine: clickhouse ? 'MergeTree' : 'Greengage',
    description: '', owner: '', columns: clickhouse
      ? [{ name: 'event_date', type: 'Date', nullable: false, key: true }, { name: 'value', type: 'UInt64', nullable: false, key: false }]
      : [{ name: 'entity_hk', type: 'bytea', nullable: false, key: true }, { name: 'load_dts', type: 'timestamp', nullable: false, key: false }],
    orderBy: clickhouse ? ['event_date'] : [],
  })
  const [tab, setTab] = useState('general')
  const [saving, setSaving] = useState(false)
  const update = (key, value) => setForm((old) => ({ ...old, [key]: value }))
  const updateColumn = (index, key, value) => setForm((old) => {
    const previousName = old.columns[index].name
    return {
      ...old,
      columns: old.columns.map((col, i) => i === index ? { ...col, [key]: value } : col),
      orderBy: key === 'name' ? (old.orderBy || []).map((name) => name === previousName ? value : name) : old.orderBy,
    }
  })
  const removeColumn = (index) => setForm((old) => {
    const removedName = old.columns[index].name
    return {
      ...old,
      columns: old.columns.filter((_, i) => i !== index),
      orderBy: (old.orderBy || []).filter((name) => name !== removedName),
    }
  })
  const toggleOrderBy = (name) => setForm((old) => ({
    ...old,
    orderBy: (old.orderBy || []).includes(name)
      ? old.orderBy.filter((value) => value !== name)
      : [...(old.orderBy || []), name],
  }))
  const submit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try { await onSave(layer, form, !!initial) } finally { setSaving(false) }
  }
  return (
    <Modal onClose={onClose} size="large">
      <form onSubmit={submit}>
        <div className="modal-header">
          <div className={`modal-icon ${clickhouse ? 'cyan' : 'violet'}`}><Table2 size={21} /></div>
          <div><span className="kicker">{clickhouse ? 'CLICKHOUSE' : 'GREEN GAGE'}</span><h2>{initial ? 'Редактировать таблицу' : clickhouse ? 'Новая витрина' : 'Новая Business Vault таблица'}</h2></div>
          <button type="button" className="icon-button" onClick={onClose}><X size={19} /></button>
        </div>
        <div className="modal-tabs">
          <button type="button" className={tab === 'general' ? 'active' : ''} onClick={() => setTab('general')}>Основное</button>
          <button type="button" className={tab === 'columns' ? 'active' : ''} onClick={() => setTab('columns')}>Колонки <span>{form.columns.length}</span></button>
          {clickhouse && <button type="button" className={tab === 'engine' ? 'active' : ''} onClick={() => setTab('engine')}>Движок</button>}
        </div>
        <div className="modal-body">
          {tab === 'general' && (
            <div className="form-grid">
              <Field label="Схема" required>{clickhouse ? (
                <select value={form.schema} onChange={(e) => update('schema', e.target.value)} required>
                  {availableSchemas.map((schema) => <option key={schema} value={schema}>{schema}</option>)}
                </select>
              ) : <input value={form.schema} onChange={(e) => update('schema', e.target.value)} placeholder="business_vault" required />}</Field>
              <Field label="Имя таблицы" required><input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="customer_360" pattern="[A-Za-z_][A-Za-z0-9_]*" required /></Field>
              <Field label="Тип сущности"><select value={form.kind} onChange={(e) => update('kind', e.target.value)}>{(clickhouse ? ['Mart', 'Aggregate', 'Dictionary'] : ['PIT', 'Bridge', 'Aggregate', 'Business table']).map((v) => <option key={v}>{v}</option>)}</select></Field>
              <Field label="Владелец"><input value={form.owner} onChange={(e) => update('owner', e.target.value)} placeholder="Команда или домен" /></Field>
              <Field label="Описание" wide><textarea value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="Для чего нужна эта таблица и какие данные содержит…" rows="4" /></Field>
            </div>
          )}
          {tab === 'columns' && (
            <div className="column-editor">
              <div className="column-editor-head"><span>Имя</span><span>Тип данных</span><span>NULL</span><span>Ключ</span><span /></div>
              {form.columns.map((col, index) => (
                <div className="column-editor-row" key={index}>
                  <input value={col.name} onChange={(e) => updateColumn(index, 'name', e.target.value)} placeholder="column_name" required />
                  <select className="column-type-select" value={col.type} onChange={(e) => updateColumn(index, 'type', e.target.value)} required>
                    {!dataTypes.includes(col.type) && <option value={col.type}>{col.type}</option>}
                    {dataTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                  </select>
                  <button
                    type="button"
                    className={`boolean-control ${col.nullable ? 'active' : ''}`}
                    aria-pressed={col.nullable}
                    title={col.nullable ? 'NULL разрешён — нажмите, чтобы запретить' : 'NULL запрещён — нажмите, чтобы разрешить'}
                    onClick={() => updateColumn(index, 'nullable', !col.nullable)}
                  >
                    {col.nullable ? <Check size={12} /> : <X size={11} />}
                    <span>{col.nullable ? 'Да' : 'Нет'}</span>
                  </button>
                  <button
                    type="button"
                    className={`boolean-control ${col.key ? 'active key' : ''}`}
                    aria-pressed={col.key}
                    title={col.key ? 'Ключевое поле — нажмите, чтобы снять' : 'Нажмите, чтобы сделать поле ключевым'}
                    onClick={() => updateColumn(index, 'key', !col.key)}
                  >
                    {col.key ? <Check size={12} /> : <X size={11} />}
                    <span>{col.key ? 'Да' : 'Нет'}</span>
                  </button>
                  <button type="button" className="icon-button danger-ghost" disabled={form.columns.length === 1} onClick={() => removeColumn(index)}><Trash2 size={15} /></button>
                </div>
              ))}
              <button type="button" className="add-column-button" onClick={() => update('columns', [...form.columns, { name: '', type: clickhouse ? 'String' : 'text', nullable: true, key: false }])}><Plus size={15} />Добавить колонку</button>
            </div>
          )}
          {tab === 'engine' && (
            <div className="form-grid">
              <Field label="ClickHouse Engine" wide><select value={form.engine} onChange={(e) => update('engine', e.target.value)}>{['MergeTree', 'ReplacingMergeTree', 'SummingMergeTree', 'AggregatingMergeTree'].map((v) => <option key={v}>{v}</option>)}</select></Field>
              <Field label="ORDER BY" wide hint="Выберите одну или несколько колонок. Номер показывает порядок ключа сортировки.">
                <div className="order-multiselect">
                  {form.columns.filter((column) => column.name.trim()).map((column) => {
                    const position = (form.orderBy || []).indexOf(column.name)
                    return (
                      <button key={column.name} type="button" className={position >= 0 ? 'selected' : ''} onClick={() => toggleOrderBy(column.name)}>
                        <span className="order-check">{position >= 0 ? position + 1 : <Plus size={12} />}</span>
                        <span>{column.name}</span>
                        <code>{column.type}</code>
                      </button>
                    )
                  })}
                  {!form.columns.some((column) => column.name.trim()) && <span className="order-empty">Сначала добавьте и назовите колонки</span>}
                </div>
              </Field>
              <div className="engine-note"><Sparkles size={18} /><div><strong>Подсказка по сортировке</strong><p>Сначала укажите колонки, которые чаще всего используются в фильтрах, начиная с полей с меньшей кардинальностью.</p></div></div>
            </div>
          )}
        </div>
        <div className="modal-footer"><button type="button" className="secondary-button" onClick={onClose}>Отмена</button><button className="primary-button" disabled={saving}>{saving && <RefreshCw size={15} className="spin" />}{initial ? 'Сохранить изменения' : 'Создать таблицу'}</button></div>
      </form>
    </Modal>
  )
}

function PipelineModal({ initial, source, sourceLayer, tables, onClose, onSave }) {
  const initialSourceLayer = sourceLayer || initial?.sourceLayer || 'data-vault'
  const getSourceName = () => {
    if (source) return `${source.schema}.${source.name}`
    if (initial?.sourceTable) return initial.sourceTable
    return tables.dv[0] ? `${tables.dv[0].schema}.${tables.dv[0].name}` : ''
  }
  const [form, setForm] = useState(() => initial ? structuredClone(initial) : {
    name: '', description: '', sourceLayer: initialSourceLayer, sourceTable: getSourceName(),
    targetLayer: initialSourceLayer === 'business-vault' ? 'data-mart' : 'business-vault', targetTable: '',
    sql: '', transferMode: 'direct', fileFormat: 'Parquet', columnMappings: [],
    schedule: { enabled: true, cron: '0 * * * *', timezone: 'Asia/Yekaterinburg' },
  })
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [validation, setValidation] = useState(null)
  const [mappingValidation, setMappingValidation] = useState(null)
  const [mappingChecking, setMappingChecking] = useState(false)
  const mappingValidationRun = useRef(0)
  const update = (key, value) => setForm((old) => ({ ...old, [key]: value }))
  const availableSources = form.sourceLayer === 'data-vault' ? tables.dv : tables.bv
  const availableTargets = form.targetLayer === 'business-vault' ? tables.bv : tables.marts
  const s3Available = form.sourceLayer === 'business-vault' && form.targetLayer === 'data-mart'
  const target = availableTargets.find((table) => `${table.schema}.${table.name}` === form.targetTable)
  const setTransferMode = (mode) => {
    mappingValidationRun.current += 1
    setMappingValidation(null)
    setForm((old) => ({
      ...old, transferMode: mode, fileFormat: 'Parquet',
      columnMappings: mode === 's3' ? (target?.columns || []).map((column) => ({ source: column.name, target: column.name })) : [],
    }))
  }
  const setTarget = (value) => {
    const selected = availableTargets.find((table) => `${table.schema}.${table.name}` === value)
    mappingValidationRun.current += 1
    setMappingValidation(null)
    setForm((old) => ({
      ...old, targetTable: value,
      columnMappings: old.transferMode === 's3' ? (selected?.columns || []).map((column) => ({ source: column.name, target: column.name })) : old.columnMappings,
    }))
  }
  const updateMapping = (index, key, value) => { mappingValidationRun.current += 1; setMappingValidation(null); setForm((old) => ({ ...old, columnMappings: (old.columnMappings || []).map((mapping, i) => i === index ? { ...mapping, [key]: value } : mapping) })) }
  const removeMapping = (index) => { mappingValidationRun.current += 1; setMappingValidation(null); setForm((old) => ({ ...old, columnMappings: old.columnMappings.filter((_, i) => i !== index) })) }
  const addMapping = () => {
    const unused = target?.columns.find((column) => !(form.columnMappings || []).some((mapping) => mapping.target === column.name))
    if (unused) { mappingValidationRun.current += 1; setMappingValidation(null); setForm((old) => ({ ...old, columnMappings: [...(old.columnMappings || []), { source: unused.name, target: unused.name }] })) }
  }
  const validate = async () => {
    try { setValidation(await api.post('/sql/validate', { sql: form.sql })) } catch (err) { setValidation({ valid: false, errors: [err.message] }) }
  }
  const synchronizeMappings = (mappings, resultColumns) => {
    const resultNames = new Set(resultColumns.map((column) => column.name))
    const used = new Set(mappings.filter((mapping) => resultNames.has(mapping.source)).map((mapping) => mapping.source))
    const changes = []
    const synchronized = mappings.map((mapping, index) => {
      if (resultNames.has(mapping.source)) return mapping
      const byTarget = resultColumns.find((column) => column.name === mapping.target && !used.has(column.name))
      const byPosition = resultColumns[index] && !used.has(resultColumns[index].name) ? resultColumns[index] : null
      const candidate = byTarget || byPosition || resultColumns.find((column) => !used.has(column.name))
      if (!candidate) return mapping
      used.add(candidate.name)
      changes.push({ from: mapping.source, to: candidate.name, target: mapping.target })
      return { ...mapping, source: candidate.name }
    })
    return { mappings: synchronized, changes }
  }
  const validateMapping = async () => {
    const run = ++mappingValidationRun.current
    setMappingChecking(true)
    try {
      let validatedForm = form
      let result = await api.post('/pipelines/validate-mapping', validatedForm)
      if (run !== mappingValidationRun.current) return { result, validatedForm }
      if (result.resultColumns?.length) {
        const synchronization = synchronizeMappings(validatedForm.columnMappings || [], result.resultColumns)
        if (synchronization.changes.length) {
          validatedForm = { ...validatedForm, columnMappings: synchronization.mappings }
          setForm(validatedForm)
          result = await api.post('/pipelines/validate-mapping', validatedForm)
          if (run !== mappingValidationRun.current) return { result, validatedForm }
          result = { ...result, autoChanges: synchronization.changes }
        }
      }
      setMappingValidation(result)
      return { result, validatedForm }
    } catch (err) {
      const result = { valid: false, sourceColumns: [], resultColumns: [], targetColumns: [], errors: [err.message] }
      setMappingValidation(result)
      return { result, validatedForm: form }
    } finally { if (run === mappingValidationRun.current) setMappingChecking(false) }
  }
  useEffect(() => {
    if (step !== 2 || form.transferMode !== 's3' || !form.sql.trim() || !form.targetTable) return undefined
    const timer = window.setTimeout(() => { void validateMapping() }, 900)
    return () => window.clearTimeout(timer)
  }, [form.sql, form.sourceTable, form.targetTable, form.transferMode, step])
  const submit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (form.transferMode === 's3') {
        const checked = await validateMapping()
        if (!checked.result.valid) { setStep(2); return }
        await onSave(checked.validatedForm, !!initial)
        return
      }
      await onSave(form, !!initial)
    } finally { setSaving(false) }
  }
  return (
    <Modal onClose={onClose} size="xlarge">
      <form onSubmit={submit}>
        <div className="modal-header">
          <div className="modal-icon violet"><GitBranch size={21} /></div>
          <div><span className="kicker">SQL PIPELINE</span><h2>{initial ? 'Редактировать пайплайн' : 'Новый пайплайн'}</h2></div>
          <button type="button" className="icon-button" onClick={onClose}><X size={19} /></button>
        </div>
        <div className="stepper">
          {[['1', 'Источник и цель'], ['2', 'SQL-трансформация'], ['3', 'Расписание']].map(([id, label], index) => <React.Fragment key={id}><button type="button" className={step >= Number(id) ? 'active' : ''} onClick={() => setStep(Number(id))}><span>{step > Number(id) ? <Check size={14} /> : id}</span>{label}</button>{index < 2 && <i className={step > Number(id) ? 'active' : ''} />}</React.Fragment>)}
        </div>
        <div className="modal-body pipeline-form-body">
          {step === 1 && (
            <div className="pipeline-setup">
              <div className="form-grid">
                <Field label="Название" wide required><input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="DV → Customer 360" required /></Field>
                <Field label="Описание" wide><textarea value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="Что делает этот пайплайн…" rows="3" /></Field>
              </div>
              <div className="source-target-grid">
                <div className="endpoint-card source">
                  <div className="endpoint-title"><div><Database size={17} /></div><span>ИСТОЧНИК</span></div>
                  <Field label="Слой"><select value={form.sourceLayer} onChange={(e) => { const layer = e.target.value; setForm((old) => ({ ...old, sourceLayer: layer, targetLayer: layer === 'data-vault' ? 'business-vault' : 'data-mart', transferMode: layer === 'business-vault' ? old.transferMode : 'direct', columnMappings: layer === 'business-vault' ? old.columnMappings : [] })) }}><option value="data-vault">Data Vault · Greengage</option><option value="business-vault">Business Vault · Greengage</option></select></Field>
                  <Field label="Таблица"><select value={form.sourceTable} onChange={(e) => update('sourceTable', e.target.value)}><option value="">Выберите таблицу</option>{availableSources.map((t) => <option key={t.id} value={`${t.schema}.${t.name}`}>{t.schema}.{t.name}</option>)}</select></Field>
                </div>
                <div className="endpoint-arrow"><ArrowDown size={18} /></div>
                <div className="endpoint-card target">
                  <div className="endpoint-title"><div><Boxes size={17} /></div><span>НАЗНАЧЕНИЕ</span></div>
                  <Field label="Слой"><select value={form.targetLayer} onChange={(e) => { const layer = e.target.value; setForm((old) => ({ ...old, targetLayer: layer, targetTable: '', transferMode: layer === 'data-mart' && old.sourceLayer === 'business-vault' ? old.transferMode : 'direct', columnMappings: [] })) }}><option value="business-vault">Business Vault · Greengage</option><option value="data-mart">Data Mart · ClickHouse</option></select></Field>
                  <Field label="Таблица"><select value={form.targetTable} onChange={(e) => setTarget(e.target.value)} required><option value="">Выберите таблицу</option>{availableTargets.map((t) => <option key={t.id} value={`${t.schema}.${t.name}`}>{t.schema}.{t.name}</option>)}</select></Field>
                </div>
              </div>
              <div className="transfer-mode-panel">
                <span>Способ переноса</span>
                <button type="button" className={(form.transferMode || 'direct') === 'direct' ? 'selected' : ''} onClick={() => setTransferMode('direct')}><Zap size={17} /><div><strong>Прямой INSERT</strong><small>ClickHouse читает источник напрямую</small></div></button>
                <button type="button" disabled={!s3Available} className={form.transferMode === 's3' ? 'selected' : ''} onClick={() => setTransferMode('s3')}><HardDrive size={17} /><div><strong>Через S3-файл</strong><small>SELECT → Parquet → ClickHouse</small></div></button>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="sql-step">
              <div className="sql-toolbar"><div><Code2 size={17} /><span>{form.transferMode === 's3' ? 'SELECT для выгрузки в Parquet' : 'SQL-трансформация'}</span></div><div><span className="dialect-badge">{form.targetLayer === 'data-mart' ? 'ClickHouse SQL' : 'Greenplum SQL'}</span>{form.transferMode !== 's3' && <button type="button" className="secondary-button compact-button" onClick={validate}><ShieldCheck size={15} />Проверить</button>}</div></div>
              <div className="sql-editor">
                <div className="line-numbers">{Array.from({ length: Math.max(12, form.sql.split('\n').length) }, (_, i) => <span key={i}>{i + 1}</span>)}</div>
                <textarea value={form.sql} onChange={(e) => { mappingValidationRun.current += 1; update('sql', e.target.value); setValidation(null); setMappingValidation(null) }} spellCheck="false" placeholder={form.transferMode === 's3' ? `SELECT customer_id, segment, ltv\nFROM postgresql(\n  'greengage:5432', 'dwh', 'source_table',\n  ':greengage_user', ':greengage_password', 'business_vault'\n)` : `INSERT INTO ${form.targetTable || 'schema.target_table'}\nSELECT ...\nFROM ${form.sourceTable || 'schema.source_table'}\nWHERE load_dts > :last_success_at;`} required />
              </div>
              {validation && <div className={`validation-box ${validation.valid ? 'valid' : 'invalid'}`}>{validation.valid ? <CheckCircle2 size={17} /> : <AlertCircle size={17} />}<div><strong>{validation.valid ? 'SQL прошёл базовую проверку' : 'Найдены замечания'}</strong>{validation.errors?.map((err) => <span key={err}>{err}</span>)}</div></div>}
              <div className="sql-help"><span><code>:last_success_at</code> время успешного запуска</span><span><code>:execution_at</code> время текущего запуска</span></div>
              {form.transferMode === 's3' && (
                <div className="mapping-panel">
                  <div className="mapping-heading"><div><span className="kicker">PARQUET · S3</span><h3>Маппинг колонок</h3><p>Слева укажите alias из SELECT, справа выберите колонку целевой витрины.</p></div><div className="mapping-actions"><button type="button" className="secondary-button compact-button" onClick={validateMapping} disabled={mappingChecking || !form.sql || !target}>{mappingChecking ? <RefreshCw size={14} className="spin" /> : <ShieldCheck size={14} />}Проверить поля</button><button type="button" className="secondary-button compact-button" onClick={addMapping} disabled={!target || (form.columnMappings || []).length >= target.columns.length}><Plus size={14} />Добавить</button></div></div>
                  <div className="mapping-list">
                    {(form.columnMappings || []).map((mapping, index) => (
                      <div className="mapping-row" key={`${mapping.target}-${index}`}>
                        {(mappingValidation?.resultColumns || []).length ? <select value={mapping.source} onChange={(e) => updateMapping(index, 'source', e.target.value)} required><option value="">Поле результата SELECT</option>{mappingValidation.resultColumns.map((column) => <option key={column.name} value={column.name}>{column.name} · {column.type}</option>)}</select> : <input list="pipeline-result-columns" value={mapping.source} onChange={(e) => updateMapping(index, 'source', e.target.value)} placeholder="alias_из_select" pattern="[A-Za-z_][A-Za-z0-9_]*" required />}
                        <ArrowRight size={16} />
                        <select value={mapping.target} onChange={(e) => updateMapping(index, 'target', e.target.value)} required><option value="">Колонка витрины</option>{(target?.columns || []).map((column) => <option key={column.name} value={column.name}>{column.name} · {column.type}</option>)}</select>
                        <button type="button" className="icon-button danger-ghost" onClick={() => removeMapping(index)}><Trash2 size={14} /></button>
                      </div>
                    ))}
                    <datalist id="pipeline-result-columns">{(mappingValidation?.resultColumns || []).map((column) => <option key={column.name} value={column.name}>{column.type}</option>)}</datalist>
                    {!(form.columnMappings || []).length && <div className="mapping-empty">Выберите целевую витрину и добавьте хотя бы одно соответствие колонок.</div>}
                  </div>
                  {mappingValidation && <div className={`mapping-validation ${mappingValidation.valid ? 'valid' : 'invalid'}`}><div className="mapping-validation-title">{mappingValidation.valid ? <CheckCircle2 size={17} /> : <AlertCircle size={17} />}<strong>{mappingValidation.valid ? 'Исходные поля, SELECT и маппинг валидны' : 'Маппинг требует исправления'}</strong></div>{mappingValidation.autoChanges?.map((change) => <span className="mapping-auto-change" key={`${change.target}-${change.from}`}>Автоматически заменено: <code>{change.from}</code> → <code>{change.to}</code> для колонки <code>{change.target}</code></span>)}{mappingValidation.errors?.map((err) => <span key={err}>{err}</span>)}{!!mappingValidation.sourceColumns?.length && <div className="validation-columns"><b>Исходная таблица</b>{mappingValidation.sourceColumns.map((column) => <code key={column.name}>{column.name} · {column.type}</code>)}</div>}{!!mappingValidation.resultColumns?.length && <div className="validation-columns"><b>Результат SELECT</b>{mappingValidation.resultColumns.map((column) => <code key={column.name}>{column.name} · {column.type}</code>)}</div>}</div>}
                </div>
              )}
            </div>
          )}
          {step === 3 && (
            <div className="schedule-step">
              <div className="schedule-enable">
                <div className="schedule-panel-icon"><CalendarClock size={19} /></div>
                <div><strong>Запускать по расписанию</strong><span>Пайплайн можно будет запускать вручную в любое время</span></div>
                <button type="button" className={`switch ${form.schedule.enabled ? 'on' : ''}`} onClick={() => update('schedule', { ...form.schedule, enabled: !form.schedule.enabled })}><span /></button>
              </div>
              <div className={`schedule-fields ${!form.schedule.enabled ? 'disabled' : ''}`}>
                <Field label="Cron-выражение" hint="Минута · Час · День · Месяц · День недели"><div className="input-with-icon"><Code2 size={16} /><input value={form.schedule.cron} onChange={(e) => update('schedule', { ...form.schedule, cron: e.target.value })} disabled={!form.schedule.enabled} placeholder="0 * * * *" /></div></Field>
                <Field label="Часовой пояс"><select value={form.schedule.timezone} onChange={(e) => update('schedule', { ...form.schedule, timezone: e.target.value })} disabled={!form.schedule.enabled}>{['Asia/Yekaterinburg', 'Europe/Moscow', 'UTC', 'Asia/Almaty'].map((v) => <option key={v}>{v}</option>)}</select></Field>
                <div className="cron-presets">
                  <span>Быстрый выбор</span>
                  {[['Каждый час', '0 * * * *'], ['Каждые 4 часа', '0 */4 * * *'], ['Ежедневно 02:00', '0 2 * * *'], ['Пн–Пт 08:00', '0 8 * * 1,2,3,4,5']].map(([label, cron]) => <button type="button" key={cron} onClick={() => update('schedule', { ...form.schedule, cron })} disabled={!form.schedule.enabled}>{label}<code>{cron}</code></button>)}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="secondary-button" onClick={step === 1 ? onClose : () => setStep(step - 1)}>{step === 1 ? 'Отмена' : 'Назад'}</button>
          {step < 3 ? <button type="button" className="primary-button" onClick={() => setStep(step + 1)}>Продолжить<ArrowRight size={16} /></button> : <button className="primary-button" disabled={saving}>{saving && <RefreshCw size={15} className="spin" />}{initial ? 'Сохранить изменения' : 'Создать пайплайн'}</button>}
        </div>
      </form>
    </Modal>
  )
}

function Modal({ children, onClose, size = '' }) {
  return <div className="modal-layer"><div className="modal-backdrop" onClick={onClose} /><div className={`modal ${size}`}>{children}</div></div>
}

function ConfirmModal({ title, text, confirm, onClose, onConfirm }) {
  return (
    <Modal onClose={onClose}>
      <div className="confirm-modal">
        <div className="confirm-icon"><Trash2 size={22} /></div><h2>{title}</h2><p>{text}</p>
        <div className="confirm-note"><AlertCircle size={16} />Убедитесь, что зависимые пайплайны остановлены.</div>
        <div className="modal-footer"><button className="secondary-button" onClick={onClose}>Отмена</button><button className="danger-button" onClick={onConfirm}><Trash2 size={15} />{confirm}</button></div>
      </div>
    </Modal>
  )
}

function CodePreview({ code }) {
  return <div className="code-preview"><div className="code-preview-top"><span><i className="red" /><i className="amber" /><i className="green" /></span><button><ExternalLink size={13} />Открыть в редакторе</button></div><pre>{code}</pre></div>
}

function Field({ label, hint, required, wide, children }) {
  return <label className={`field ${wide ? 'wide' : ''}`}><span>{label}{required && <b>*</b>}</span>{children}{hint && <small>{hint}</small>}</label>
}

function Property({ label, value, wide }) {
  return <div className={`property ${wide ? 'wide' : ''}`}><span>{label}</span><strong>{value}</strong></div>
}

function LayerPill({ layer }) {
  const label = layer === 'data-vault' ? 'Data Vault' : layer === 'business-vault' ? 'Business Vault' : 'Data Mart'
  return <span className={`layer-pill ${layer}`}>{label}</span>
}

function StatusBadge({ status }) {
  const labels = { healthy: 'Healthy', warning: 'Warning', failed: 'Ошибка', active: 'Активен', paused: 'На паузе', success: 'Успешно', running: 'Выполняется' }
  return <span className={`status-badge ${status}`}><i />{labels[status] || status}</span>
}

function SummaryChip({ icon: Icon, label, value, tone }) {
  return <div className="summary-chip"><div className={`summary-icon ${tone}`}><Icon size={17} /></div><span>{label}</span><strong>{value}</strong></div>
}

function Toast({ message, tone }) {
  return <div className={`toast ${tone}`}>{tone === 'error' ? <AlertCircle size={18} /> : tone === 'info' ? <Activity size={18} /> : <CheckCircle2 size={18} />}<span>{message}</span></div>
}

function ErrorBanner({ message, retry }) {
  return <div className="error-banner"><AlertCircle size={19} /><div><strong>API недоступен</strong><span>{message}. Запустите Go backend на порту 8090.</span></div><button className="secondary-button" onClick={retry}><RefreshCw size={15} />Повторить</button></div>
}

function EmptyState({ icon: Icon, title, text }) {
  return <div className="empty-state"><div><Icon size={22} /></div><strong>{title}</strong><span>{text}</span></div>
}

function EmptyInline({ icon: Icon, text }) {
  return <div className="empty-inline"><Icon size={18} /><span>{text}</span></div>
}

function PageSkeleton() {
  return <div className="page-stack skeleton-page"><div className="metrics-grid">{[1, 2, 3, 4].map((i) => <div className="metric-card skeleton" key={i} />)}</div><div className="architecture-card skeleton tall" /><div className="overview-columns"><div className="card skeleton tall" /><div className="card skeleton tall" /></div></div>
}

function formatNumber(value = 0) { return new Intl.NumberFormat('ru-RU').format(value) }
function formatCompact(value = 0) { return new Intl.NumberFormat('ru-RU', { notation: 'compact', maximumFractionDigits: 1 }).format(value) }
function formatBytes(value = 0) {
  if (!value) return '0 Б'
  const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ', 'ПБ']
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
  const amount = value / (1024 ** index)
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: amount >= 100 ? 0 : 1 }).format(amount)} ${units[index]}`
}
function formatPercent(value = 0) { return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value) }
function formatUptime(seconds = 0) {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (days) return `${days} д ${hours} ч`
  if (hours) return `${hours} ч ${minutes} мин`
  return `${minutes} мин`
}
function formatDuration(ms = 0) { if (ms < 1000) return `${ms} мс`; if (ms < 60000) return `${Math.round(ms / 100) / 10} сек`; return `${Math.floor(ms / 60000)} мин ${Math.round((ms % 60000) / 1000)} сек` }
function formatRelative(value) {
  if (!value) return '—'
  const diff = new Date(value).getTime() - Date.now()
  const abs = Math.abs(diff)
  const rtf = new Intl.RelativeTimeFormat('ru', { numeric: 'auto' })
  if (abs < 60000) return 'только что'
  if (abs < 3600000) return rtf.format(Math.round(diff / 60000), 'minute')
  if (abs < 86400000) return rtf.format(Math.round(diff / 3600000), 'hour')
  return rtf.format(Math.round(diff / 86400000), 'day')
}
function formatDateTime(value) { return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(value)) }
function formatClock(value) { return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(new Date(value)) }
function formatDay(value) { return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short' }).format(new Date(value)) }
function cronLabel(cron) {
  const known = { '0 * * * *': 'Каждый час', '0 */4 * * *': 'Каждые 4 часа', '15 2 * * *': 'Ежедневно в 02:15', '0 2 * * *': 'Ежедневно в 02:00', '30 */4 * * *': 'Каждые 4 часа' }
  return known[cron] || cron
}
function pluralTable(n) { const mod10 = n % 10; const mod100 = n % 100; return mod10 === 1 && mod100 !== 11 ? 'таблица' : mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14) ? 'таблицы' : 'таблиц' }
function pluralPipeline(n) { const mod10 = n % 10; const mod100 = n % 100; return mod10 === 1 && mod100 !== 11 ? 'пайплайн' : mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14) ? 'пайплайна' : 'пайплайнов' }
function toneForKind(kind) { return ({ Hub: 'blue', Satellite: 'cyan', Link: 'amber', PIT: 'violet', Bridge: 'pink', Aggregate: 'green', Mart: 'cyan', Dictionary: 'amber' })[kind] || 'neutral' }

createRoot(document.getElementById('root')).render(<React.StrictMode><App /></React.StrictMode>)
